import { RoleType } from '../services/api/user/userInfo';

const API_SERVICE = process.env.REACT_APP_API_SERVICE;

export const DASHBOARD_API = Object.freeze({
  ProjectList: `${API_SERVICE}/projects`, // GET
  ProjectSearch: `${API_SERVICE}/projects/search`, // POST
  ProjectPictureUrl: `${API_SERVICE}/{0}`, // GET, 0: imgUrl(string)
  ProjectById: (projectId: number | string) =>
    `${API_SERVICE}/projects/${projectId}`, // GET
  ProjectMemberPositions: `${API_SERVICE}/dictionary/project-position`,
  TelescopeUsers: (name: string) => `${API_SERVICE}/users?query=${name}`,
  SkilloTechs: (skill: string, numOfRes: number) =>
    `${API_SERVICE}/skill/lookup?size=${numOfRes}&parents=Engineering Practices,Technologies&duplicate=false&q=${skill}`,
  SkilloTechsByID: (ids: string) => `${API_SERVICE}/skill/${ids}`,
  GetIndustries: (query: string) => `${API_SERVICE}/industries?query=${query}`, // GET
  DeleteProjectCoverImg: (id: number) =>
    `${API_SERVICE}/projects/${id}/picture`, // DELETE - Request to delete project cover img
  GetProjectStatusById: (id: number) => `${API_SERVICE}/projects/${id}/status`, //GET
  RestoreProjectById: (id: number) => `${API_SERVICE}/projects/${id}/restore`,
});

export const USER_API = Object.freeze({
  Info: `${API_SERVICE}/loggedin`, // GET
  UserProfilePicture: `${API_SERVICE}/users/{0}/picture`, // GET, 0: userId(string/number)
});

export const RepoAPI = Object.freeze({
  getProjectRepoFiles: (projectId: string) =>
    `${API_SERVICE}/repofiles/${projectId}`,
});

export const ProjectManagementAPI = Object.freeze({
  ProjectLinks: `${API_SERVICE}/dictionary/project-link-type`, // GET
  CreateProject: `${API_SERVICE}/projects`,
  UpdateProject: (id: number) => `${API_SERVICE}/projects/${id}`,
  UpdateProjectEndDate: (id: number) =>
    `${API_SERVICE}/projects/${id}/plannedEndDate`,
  DeleteProject: (id: number) => `${API_SERVICE}/projects/project/${id}`,
  UpdateProjectStatus: (id: string | number, status: string) =>
    `${API_SERVICE}/projects/${id}/status?status=${status}`, // POST
  AddOpportunity: (id: number) => `${API_SERVICE}/projects/${id}/opportunity`, // POST-Add opportunity to specific project
  GetFieldsStatusToAddProjectToShowroom: (id: number, link?: string) =>
    `${API_SERVICE}/projects/${id}/showroom-request${
      link ? `?link=${link}` : ''
    }`, // GET- project fields status
  AddShowroomRequest: (id: number) =>
    `${API_SERVICE}/projects/${id}/showroom-request`, //POST-Request to add project to showroom
  RemoveShowroomRequest: (id: number) =>
    `${API_SERVICE}/projects/project/${id}/showroom`, //DELETE-Request to remove project from showroom
  TransferOwnership: (projectId: number | string) =>
    `${API_SERVICE}/projects/${projectId}/membership/owner`,
  AcceptShowroomRequest: (id: number) =>
    `${API_SERVICE}/showroom_requests/${id}/accept`, // GET-Showroom request accept
  DeclineShowroomRequest: (id: number) =>
    `${API_SERVICE}/showroom_requests/${id}/decline`, // GET-Showroom request decline
  RevokeShowroomRequest: (id: number) =>
    `${API_SERVICE}/showroom_requests/${id}/revoke`, // GET-Showroom request revoke

  //Hitting below URLs (GetContributionReport, GetProjectReport) directly will download a Report
  GetContributionReport: (
    excelVersion: string | any,
    since: string,
    until: string
  ) =>
    `${API_SERVICE}/projects/contribution_report/excel?excel%20version=${excelVersion}&since=${since}&until=${until}`,
  GetProjectReport: (excelVersion: string | any) =>
    `${API_SERVICE}/projects/project_report/excel?excel%20version=${excelVersion}`,
});

export const DICT_API = Object.freeze({
  GetDictionary: (dictionary: string) =>
    `${API_SERVICE}/dictionary/${dictionary}`,
});

export const OPPORTUNITY_API = Object.freeze({
  DeleteOpportunity: (id: number) => `${API_SERVICE}/opportunities/${id}`, //DEL-OPPORTUNITY with Opportunity ID
  EditOpportunity: (id: number) => `${API_SERVICE}/opportunities/${id}`, //Edit Opportunity with opportunity ID
  RefreshOpportunity: (id: number) =>
    `${API_SERVICE}/opportunities/${id}/refresh`, //Refresh Opportunity with opportunity ID
  GetOpportunities: (
    amount: number,
    startPoint: number,
    searchPhrase: string,
    positions?: string[],
    technologies?: string[],
    challenges?: string[]
  ) => {
    const positionsQueryParam = positions?.length
      ? `&positions=${positions.join(',')}`
      : '';
    const challengesQueryParam = challenges?.length
      ? `&challenges=${challenges?.join(',')}`
      : '';
    const technologiesQueryParam = technologies?.length
      ? `&technologies=${technologies.map(encodeURIComponent).join(',')}`
      : '';
    return `${API_SERVICE}/opportunities?offset.amount=${amount}&offset.startPoint=${startPoint}&searchPhrase=${searchPhrase}${positionsQueryParam}${technologiesQueryParam}${challengesQueryParam}`;
  },
  RequestToJoinOpportunity: (id: number) =>
    `${API_SERVICE}/opportunities/${id}/join`,
  GetSubscription: `${API_SERVICE}/opportunities/subscribe`, // GET
  EditSubscription: `${API_SERVICE}/opportunities/subscribe`, // PUT
  GenerateOpportunityReport: (excelVersion: string) =>
    `${API_SERVICE}/opportunities/opportunity_report/excel?excel%20version=${excelVersion}`,
});

export const MEMBER_API = Object.freeze({
  GetMembershipRequest: (id: number) =>
    `${API_SERVICE}/membership_requests/${id}`,
  AcceptMembershipRequest: (id: number, comment?: string) =>
    `${API_SERVICE}/membership_requests/${id}/accept?comment=${comment ?? ''}`,
  DeclineMembershipRequest: (id: number, comment?: string) =>
    `${API_SERVICE}/membership_requests/${id}/decline?comment=${comment ?? ''}`,
  RevokeMembershipRequest: (id: number) =>
    `${API_SERVICE}/membership_requests/${id}/revoke`,
  DeleteProjectMember: (projectId: number | string, externalUserId: string) =>
    `${API_SERVICE}/projects/${projectId}/membership/${externalUserId}`,
  AddProjectMember: (projectId: number | string) =>
    `${API_SERVICE}/projects/${projectId}/membership`,
  EditProjectMember: (projectId: number | string, memberId: string) =>
    `${API_SERVICE}/projects/${projectId}/membership/${memberId}`,
});

export const GARAGE_USER_API = Object.freeze({
  getListGarageUsers: ({
    amount,
    startPoint,
    searchPhrase,
  }: {
    amount: number;
    startPoint: number;
    searchPhrase?: string;
  }) =>
    `${API_SERVICE}/users/local?offset.amount=${amount}&offset.startPoint=${startPoint}${
      (searchPhrase || '').length > 0 ? `&searchPhrase=${searchPhrase}` : ''
    }`,
  getUsersEvents: () => `${API_SERVICE}/users/events`,
});

export const PERMISSION_API = Object.freeze({
  transferSuperAdminToUser: (userID: number) =>
    `${API_SERVICE}/permissions/transfer/${userID}`,
  addPermissionToUser: (userID: number, permission: RoleType) =>
    `${API_SERVICE}/permissions/users/${userID}?permission=${permission}`,
  removePermissionToUser: (userID: number, permission: RoleType) =>
    `${API_SERVICE}/permissions/users/${userID}?permission=${permission}`,
  getUsersWithSpecifiedPermission: (permission: RoleType) =>
    `${API_SERVICE}/permissions/users/?permission=${permission}`,
  getProjectMembersPermissions: () => `${API_SERVICE}/permissions/project`,
});

export const EVENTS_API = Object.freeze({
  GetEvents: (
    amount: number,
    startPoint: number,
    searchPhrase: string,
    isValidated: boolean,
    active: boolean
  ) =>
    `${API_SERVICE}/challenges?offset.amount=${amount}&offset.startPoint=${startPoint}&searchPhrase=${searchPhrase}${
      isValidated ? '&validRegistrationDate=true' : ''
    }${active === true ? '&active=true' : ''}`,
  CreateEvent: () => `${API_SERVICE}/challenges`,
  EditEvent: (eventID: number | string) =>
    `${API_SERVICE}/challenges/${eventID}`,
  GetEventById: (eventID: number | string) =>
    `${API_SERVICE}/challenges/${eventID}`,
  DeleteEventById: (eventID: number | string) =>
    `${API_SERVICE}/challenges/${eventID}`,
  GetEventNames: () => `${API_SERVICE}/challenges/names`,
});

export const REPORTS_API = Object.freeze({
  RequestProject: (projectId: number) =>
    `${API_SERVICE}/projects/${projectId}/report`,
});

export const KEYWORDS_API = Object.freeze({
  GetKeywords: (amount: number, searchPhrase: string) =>
    `${API_SERVICE}/keywords?amount=${amount}&searchPhrase=${searchPhrase}`,
});

export const HEALTH_API = Object.freeze({
  HealthCheckStatus: `${API_SERVICE}/healthcheck-status`,
});
